import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import PageContainer from '../../../components/container/PageContainer';
import MultiInputTable from '../../../components/table/MultiInputTable';
import { RATE_HEADER } from '../../../constants/rate';
import MultiSaveButton from '../../../components/button/MultiSaveButton';
import BasePulldown from '../../../components/pulldown/BasePulldown';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRateYear,
  getRatesByYearId,
  getRateYears,
  updateRates,
} from '../../../services/api';
import { EmptyRateYearsGetResponse } from '../../../types/rate/RateYearsGetResponse';
import AddButton from '../../../components/button/AddButton';
import CsvModal from '../../../components/modal/CsvModal';
import YearPicker from '../../../components/picker/YearPicker';
import { format } from 'date-fns';
import {
  EmptyRateGetResponse,
  RateArray,
} from '../../../types/rate/RateGetResponse';
import {
  EmptyRatePutRequest,
  RatePutRequest,
} from '../../../types/rate/RatePutRequest';
import LoadingOverlay from '../../../components/LoadingOverlay';
import useValidation from '../../../hooks/useValidation';
import { RATE_VALIDATION } from '../../../constants/validation/rateValidation';
import UpdateModalOk from '../../../components/modal/UpdateModalOk';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/RateHeader.css';

const Rate = () => {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const [yearToAdd, setYearToAdd] = useState<Date | null>(null);
  const [editingData, setEditingData] =
    useState<RatePutRequest>(EmptyRatePutRequest);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { validate } = useValidation(RATE_VALIDATION);

  // テーブルの横スクロール要素（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null); // 高さ計算用

  // 年プルダウンを取得
  const {
    data: yearOptions,
    isLoading: isYearOptionsLoading,
    error: yearOptionsError,
    isError: isYearOptionsError,
  } = useQuery({
    queryKey: ['yearOptions'],
    queryFn: () => getRateYears(),
    initialData: EmptyRateYearsGetResponse,
  });

  // 期間を追加したらプルダウンを再取得
  const addRateYear = useMutation({
    mutationFn: (year: string) => createRateYear(year),
    onSuccess: () => {
      setYearToAdd(null);
      queryClient.invalidateQueries({ queryKey: ['yearOptions'] });
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  // 指定した期間のレートを取得
  const {
    data: ratesForSelectedYear,
    isLoading: isRatesLoading,
    isError: isRatesError,
    error: ratesError,
  } = useQuery({
    queryKey: ['rates', selectedYear],
    queryFn: () => getRatesByYearId(selectedYear),
    initialData: EmptyRateGetResponse,
    enabled: selectedYear !== 0, // selectedYearが0のときはクエリを実行しない
  });

  useEffect(() => {
    if (isYearOptionsError) {
      alert(yearOptionsError);
    }
    if (isRatesError) {
      alert(ratesError);
    }
  }, [isYearOptionsError, isRatesError]);

  // 取得したデータを編集用のstateにセット
  useEffect(() => {
    if (ratesForSelectedYear) {
      const putData: RatePutRequest = {
        ratesArray: ratesForSelectedYear.ratesArray.map((rate) => ({
          rateId: rate.rateId,
          rateCategoryName: rate.rateCategoryName,
          rates: { ...rate.rates },
        })),
      };
      setEditingData(putData);
    }
  }, [ratesForSelectedYear]);

  // 編集
  const editRequest = useMutation({
    mutationFn: (data: RatePutRequest) => updateRates(selectedYear, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rates', selectedYear] });
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  const handleSaveButtonClick = () => {
    for (const rate of editingData.ratesArray) {
      const errorMessage = validate(rate.rates);
      if (errorMessage) {
        alert(errorMessage);
        return;
      }
    }
    editRequest.mutate(editingData);
  };

  const handleModalOkButtonClick = () => {
    if (yearToAdd === null) {
      alert('期間を選択してください');
      return;
    }
    addRateYear.mutate(format(yearToAdd, 'yyyy'));
    setShowModal(false);
  };

  return (
    <PageContainer title="レート管理">
      <LoadingOverlay
        isLoading={isYearOptionsLoading || isRatesLoading}
        isMutating={editRequest.status === 'pending'}
      />

      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-rate gx-0">
          <Col className="header-fields-rate order-1">
            <BasePulldown
              itemList={yearOptions}
              selectedItem={selectedYear}
              defaultLabel="期間を選択"
              onChange={(id) => setSelectedYear(id)}
            />
          </Col>

          <Col xs="auto" className="header-right-rate order-2">
            <div className="actions-group-rate">
              <div className="actions-searchreset-rate">
                <AddButton
                  buttonLabel="期間"
                  onClick={() => setShowModal(true)}
                />
                <MultiSaveButton onClick={() => setShowConfirmModal(true)} />
              </div>
              <div className="actions-extra-rate">円/元</div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <MultiInputTable<RateArray>
        headers={RATE_HEADER}
        items={selectedYear !== 0 ? editingData.ratesArray : []}
        keyField="rateId"
        rowTitleField="rateCategoryName"
        dataField="rates"
        scrollContainerRef={tableRef}
        onChange={(id, field, value) => {
          setEditingData((prev) => {
            const updatedRatesArray = prev.ratesArray.map((rate) =>
              rate.rateId === id
                ? {
                    ...rate,
                    rates: {
                      ...rate.rates,
                      [field]: value === '' ? null : value,
                    },
                  }
                : rate
            );
            return { ...prev, ratesArray: updatedRatesArray };
          });
        }}
      />

      {showModal && (
        <CsvModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={() => handleModalOkButtonClick()}
          modalTitle="追加する期間を選択してください"
          saveButtonLabel="OK"
        >
          <div className="justify-content-center d-flex mt-5 mb-5">
            <YearPicker selectedYear={yearToAdd} onYearChange={setYearToAdd} />
          </div>
        </CsvModal>
      )}

      {showConfirmModal && (
        <UpdateModalOk
          show={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onSave={() => {
            handleSaveButtonClick();
            setShowConfirmModal(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default Rate;
